//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import DateString from '@models/DateString';
import TimeZoneId from '@models/TimeZoneId';
import { getWeekViewStartDate } from '@universal-scheduling/components/calendar/week/CalendarWeekViewUtils';
import { UniversalCalendarView } from '@universal-scheduling/redux/calendar/universalCalendarModel';
import { getViewDates } from '@universal-scheduling/redux/calendar/universalCalendarUtils';

/**
 * Gets the endDate at the end of the current month
 */
export const getMonthViewEndDate = (
  startDate: DateString,
  startOfWeek: number,
): DateString => {
  const startOfNextMonth = DateString.applyMomentOperation(
    getStartOfCurrentMonth(startDate),
    'add',
    1,
    'M',
  );
  const lastDayOfMonth = DateString.applyMomentOperation(
    startOfNextMonth,
    'subtract',
    1,
    'd',
  );
  const lastDaysToAdd =
    (-1 * DateString.toMoment(lastDayOfMonth, TimeZoneId.Utc).weekday() +
      startOfWeek +
      6) %
    7;
  return DateString.applyMomentOperation(
    lastDayOfMonth,
    'add',
    lastDaysToAdd,
    'd',
  );
};

/**
 * There is no guarantee that the startDate at a given time is in the month that the calendar
 * is currently representing, but there is a guarantee that the end of the week is. Returns the
 * first day of the current month shown in the calendar.
 */
export const getStartOfCurrentMonth = (startDate: DateString) => {
  const endOfStartDateWeek = DateString.addDays(startDate, 6);
  return DateString.applyMomentSetter(endOfStartDateWeek, 'date', 1);
};

/**
 * Calculates the startDate for the month view given a particular date by determining
 * the startOfWeekDay prior to the start of the month of the given date.
 */
export const getMonthViewStartDate = (
  date: DateString,
  startOfWeek: number,
) => {
  const startOfMonthDateString = DateString.applyMomentSetter(date, 'date', 1);
  return getWeekViewStartDate(startOfMonthDateString, startOfWeek);
};

export const MIN_DAY_HEIGHT_PX = '152px';

/**
 * Gets the text to display in the tooltip when dragging an item to next and last month
 */
export const getNextAndPreviousMonthsText = (
  startOfWeekDay: number,
  startDate: DateString,
) => {
  const { startDate: newStartDatePrevious } = getViewDates(
    startDate,
    startOfWeekDay,
    UniversalCalendarView.Month,
    -1,
  );
  const { startDate: newStartDateNext } = getViewDates(
    startDate,
    startOfWeekDay,
    UniversalCalendarView.Month,
    1,
  );

  const includesYearChange = (start: DateString, end: DateString) =>
    DateString.parse(start).year !== DateString.parse(end).year;

  const currentMonth = DateString.addDays(startDate, 6);
  const nextMonth = DateString.addDays(newStartDateNext, 6);
  const previousMonth = DateString.addDays(newStartDatePrevious, 6);

  const getFormat = (newMonth: DateString) =>
    includesYearChange(currentMonth, newMonth) ? 'MMMM YYYY' : 'MMMM';

  return {
    next: DateString.format(nextMonth, getFormat(nextMonth)),
    previous: DateString.format(previousMonth, getFormat(previousMonth)),
  };
};
